import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import { SchemaType } from "../../advanced-flow/schemas/schemas";
import {
  ContentGenerationContextToolType,
  contentGenerationContextToolSchema,
} from "../../content-generation-context-tool/content-generation-context-tool-definition";
import { IntegrationKind } from "./integration-kind";

// Our only supported agent is the content generator agent. If we add more agents,
// we need to add it to this enum.
export enum MarketingAgent {
  CONTENT_GENERATOR_AGENT = "contentGeneratorAgent",
}

export enum PromptCategory {
  STRATEGY = "strategy",
  TRIGGER = "trigger",
  GOAL = "goal",
  METHOD = "method",
  SCENARIO = "scenario",
  CONTEXT_TOOLS = "context_tools",
}

const baseMarketingAgentPromptSchema = z.object({
  _id: zExt.objectId(),
  agent: createEnum(MarketingAgent),
  prompt: z.string().optional(),
  displayLabel: z.string(),
  displayDescription: z.string().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  // Disable flag for disabled prompts; we'll still use old prompts for analytics
  disabledAt: z.date().nullish(),
});

// ----------------------------------------------------------------------
// Goal Schema
// ----------------------------------------------------------------------
export const marketingAgentPromptGoalSchema_ =
  baseMarketingAgentPromptSchema.extend({
    promptCategory: z.literal(PromptCategory.GOAL),
  });
export interface MarketingAgentPromptGoal
  extends z.infer<typeof marketingAgentPromptGoalSchema_> {}
export const marketingAgentPromptGoalSchema: z.ZodType<MarketingAgentPromptGoal> =
  marketingAgentPromptGoalSchema_;

// ----------------------------------------------------------------------
// Method Schema
// ----------------------------------------------------------------------
export const marketingAgentPromptMethodSchema_ =
  baseMarketingAgentPromptSchema.extend({
    promptCategory: z.literal(PromptCategory.METHOD),
  });
export interface MarketingAgentPromptMethod
  extends z.infer<typeof marketingAgentPromptMethodSchema_> {}
export const marketingAgentPromptMethodSchema: z.ZodType<MarketingAgentPromptMethod> =
  marketingAgentPromptMethodSchema_;

// ----------------------------------------------------------------------
// Scenario Schema
// ----------------------------------------------------------------------
export enum GenerationType {
  MANUAL = "MANUAL",
  AI = "AI",
}

export const marketingAgentPromptScenarioSchema_ =
  baseMarketingAgentPromptSchema.extend({
    promptCategory: z.literal(PromptCategory.SCENARIO),
    teamId: zExt.objectId(),
    smsTemplateId: zExt.objectId().optional(),
    generationType: z.enum([GenerationType.MANUAL, GenerationType.AI]),
  });
export interface MarketingAgentPromptScenario
  extends z.infer<typeof marketingAgentPromptScenarioSchema_> {}
export const marketingAgentPromptScenarioSchema: z.ZodType<MarketingAgentPromptScenario> =
  marketingAgentPromptScenarioSchema_;

// ----------------------------------------------------------------------
// Trigger Schema
// ----------------------------------------------------------------------
export const marketingAgentPromptTriggerSchema_ =
  baseMarketingAgentPromptSchema.extend({
    promptCategory: z.literal(PromptCategory.TRIGGER),
    triggerSchemaType: createEnum(SchemaType).optional(),
  });
export interface MarketingAgentPromptTrigger
  extends z.infer<typeof marketingAgentPromptTriggerSchema_> {}
export const marketingAgentPromptTriggerSchema: z.ZodType<MarketingAgentPromptTrigger> =
  marketingAgentPromptTriggerSchema_;

// ----------------------------------------------------------------------
// Strategy Schema
// ----------------------------------------------------------------------
export const marketingAgentPromptStrategySchema_ =
  baseMarketingAgentPromptSchema.extend({
    promptCategory: z.literal(PromptCategory.STRATEGY),
    goalMarketingAgentPromptId: zExt.objectId().optional(),
    methodMarketingAgentPromptId: zExt.objectId().optional(),
    triggerMarketingAgentPromptIds: z.array(zExt.objectId()).optional(),
    // @deprecated use aiContextLibraryItems instead
    examples: z.string().optional(),
    contextTools: z.array(contentGenerationContextToolSchema).optional(),
    integrations: z.array(z.enum(IntegrationKind)).optional(),
  });
export interface MarketingAgentPromptStrategy
  extends z.infer<typeof marketingAgentPromptStrategySchema_> {}
export const marketingAgentPromptStrategySchema: z.ZodType<MarketingAgentPromptStrategy> =
  marketingAgentPromptStrategySchema_;

// ----------------------------------------------------------------------
// Populated Strategy Schema
// ----------------------------------------------------------------------
// Defined AFTER Goal/Method are exported so we can reference them
export const marketingAgentPromptPopulatedSchema_ =
  marketingAgentPromptStrategySchema_.extend({
    marketingAgentPromptGoal: marketingAgentPromptGoalSchema.optional(),
    marketingAgentPromptMethod: marketingAgentPromptMethodSchema.optional(),
  });
export interface MarketingAgentPromptPopulated
  extends z.infer<typeof marketingAgentPromptPopulatedSchema_> {}
export const marketingAgentPromptPopulatedSchema: z.ZodType<MarketingAgentPromptPopulated> =
  marketingAgentPromptPopulatedSchema_;

// ----------------------------------------------------------------------
// Context Tools Schema
// ----------------------------------------------------------------------
export const marketingAgentPromptContextToolsSchema_ =
  baseMarketingAgentPromptSchema.extend({
    promptCategory: z.literal(PromptCategory.CONTEXT_TOOLS),
    requiredIntegrationKinds: z.array(z.enum(IntegrationKind)),
    contentGenerationContextToolType: createEnum(
      ContentGenerationContextToolType,
    ),
  });
export interface MarketingAgentPromptContextTools
  extends z.infer<typeof marketingAgentPromptContextToolsSchema_> {}
export const marketingAgentPromptContextToolsSchema: z.ZodType<MarketingAgentPromptContextTools> =
  marketingAgentPromptContextToolsSchema_;

// ----------------------------------------------------------------------
// Union Schema (Main)
// ----------------------------------------------------------------------
const marketingAgentPromptSchema_ = z.discriminatedUnion("promptCategory", [
  marketingAgentPromptGoalSchema_,
  marketingAgentPromptMethodSchema_,
  marketingAgentPromptScenarioSchema_,
  marketingAgentPromptTriggerSchema_,
  marketingAgentPromptStrategySchema_,
  marketingAgentPromptContextToolsSchema_,
]);
export type MarketingAgentPrompt = z.infer<typeof marketingAgentPromptSchema_>;
export const marketingAgentPromptSchema: z.ZodType<MarketingAgentPrompt> =
  marketingAgentPromptSchema_;
